import { Footer } from '@/components/footer'
import { useI18n } from '@/lib/i18n'
import { useResearchPageSeoHead } from '@/lib/research-page-seo-head'
import { currentContentLinkContext, localizeFaqAnswerHtml } from '@/pages/content-link-utils'
import englishFaq from '@/languages/en/faq.json'
import simplifiedFaq from '@/languages/zh/faq.json'
import { resolveAvailableContentLocale } from '../../functions/public-content-locale.js'

const sections: Array<{ id: string, qs: string[] }> = [
  { id: 'about', qs: ['intro', 'what', 'team'] },
  { id: 'methodology', qs: ['anti_memorization', 'why_werewolf', 'board_games', 'measure', 'leakage', 'mafia'] },
  { id: 'comparisons', qs: ['best_model', 'vs_mmlu', 'vs_lmarena', 'vs_agentbench'] },
  { id: 'using', qs: ['compare_models', 'byom', 'free', 'reports', 'play'] },
  { id: 'research', qs: ['training_data', 'agi_safety', 'cite'] },
]

const answerClass = 'text-base leading-relaxed text-slate-700 dark:text-slate-300 [&_a]:underline [&_a]:underline-offset-2 [&_a]:decoration-amber-500 [&_a:hover]:text-amber-600 [&_ul]:ml-5 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:mt-1 [&_strong]:font-semibold'

const FAQ_TRANSLATED_LANGUAGES = ['en', 'zh'] as const

function sourceFaqText(key: string, source: unknown): string {
  let value = source
  for (const segment of key.split('.')) {
    if (!value || typeof value !== 'object' || !(segment in value))
      return key
    value = (value as Record<string, unknown>)[segment]
  }
  return typeof value === 'string' ? value : key
}

export function FaqPage() {
  const { language } = useI18n()
  const contentLanguage = resolveAvailableContentLocale(
    language,
    FAQ_TRANSLATED_LANGUAGES,
  ) ?? 'en'
  const faqSource = contentLanguage === 'zh' ? simplifiedFaq : englishFaq
  const faqText = (key: string) => sourceFaqText(key, faqSource)
  const contentLinkContext = currentContentLinkContext()
  useResearchPageSeoHead({
    pagePath: '/faq',
    contentLanguage,
    title: `${faqText('faq.title')} | Mentiss AI`,
    description: faqText('faq.subtitle'),
    translatedLanguages: FAQ_TRANSLATED_LANGUAGES,
  })

  return (
    <div className="min-h-screen bg-background text-foreground">
      <main lang={contentLanguage === 'zh' ? 'zh-Hans' : 'en'}>
        <div className="container mx-auto max-w-4xl px-4 py-24 sm:px-6 lg:px-8">
          <header className="mb-12 text-center">
            <h1 className="mb-4 text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-5xl">
              {faqText('faq.title')}
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-slate-600 dark:text-slate-300">
              {faqText('faq.subtitle')}
            </p>
          </header>

          {sections.map(({ id: sectionId, qs }) => (
            <section key={sectionId} className="mb-12">
              <h2 className="mb-6 border-b border-slate-200 pb-2 text-2xl font-semibold text-slate-900 dark:border-slate-800 dark:text-slate-100">
                {faqText(`faq.sections.${sectionId}.title`)}
              </h2>
              <div className="space-y-8">
                {qs.map(qid => (
                  <article key={qid}>
                    <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
                      {faqText(`faq.sections.${sectionId}.${qid}.q`)}
                    </h3>
                    <div
                      className={answerClass}
                      dangerouslySetInnerHTML={{
                        __html: localizeFaqAnswerHtml(
                          faqText(`faq.sections.${sectionId}.${qid}.a`),
                          contentLinkContext,
                        ),
                      }}
                    />
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  )
}
